import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { conceptsService } from '@/shared/api/services/concepts.service';
import { toast } from '@/shared/lib/toast';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

interface UseConceptActionsDialogOptions {
  projectId: string;
  conceptId: string;
  renderId?: string | null;
  onDeleteConcept?: () => void;
  onDeleteAngle?: () => void;
  onDeleteRender?: (renderId: string) => void;
}

interface UseConceptActionsDialogResult {
  // Modal states (kept for API compatibility, always false)
  showDeleteConceptModal: boolean;
  showDeleteAngleModal: boolean;
  showDeleteRenderModal: boolean;

  // Actions — now execute directly instead of opening modals
  openDeleteConceptModal: () => void;
  openDeleteAngleModal: () => void;
  openDeleteRenderModal: () => void;

  // Modal closers (no-ops for API compatibility)
  setShowDeleteConceptModal: (open: boolean) => void;
  setShowDeleteAngleModal: (open: boolean) => void;
  setShowDeleteRenderModal: (open: boolean) => void;

  // Actions
  handleDeleteConcept: () => Promise<void>;
  handleDeleteAngle: () => void;
  handleDeleteRender: () => void;

  // Loading states
  isDeleting: boolean;
}

/**
 * Hook for managing delete actions in ConceptActions.
 * Concept and render deletions execute immediately with an undo toast.
 * Angle (secondary view) deletion is gated behind a confirmation modal
 * because it fans out across the shared view set and has no undo.
 */
export function useConceptActionsDialog({
  projectId,
  conceptId,
  renderId,
  onDeleteConcept,
  onDeleteAngle,
  onDeleteRender,
}: UseConceptActionsDialogOptions): UseConceptActionsDialogResult {
  const router = useRouter();
  const queryClient = useQueryClient();
  const t = useTranslations('concept');
  const tCommon = useTranslations('common');

  // Loading state
  const [isDeleting, setIsDeleting] = useState(false);

  // Angle (secondary view) delete confirmation modal state.
  // Unlike render/concept deletes (which are immediate with an undo toast),
  // deleting a secondary view fans out across the shared view set and has NO
  // undo — so it is gated behind an explicit confirmation dialog.
  const [showDeleteAngleModal, setShowDeleteAngleModal] = useState(false);

  // Delete concept handler
  const handleDeleteConcept = useCallback(async () => {
    // If callback provided (editor mode), use it — the caller's mutation has undo toast
    if (onDeleteConcept) {
      onDeleteConcept();
      return;
    }

    // Otherwise, handle delete internally (concept card mode)
    setIsDeleting(true);
    try {
      await conceptsService.deleteConcept(projectId, conceptId);

      // Show undo toast before navigating
      toast.undoable(t('conceptDeleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            try {
              await conceptsService.restoreConcept(projectId, conceptId);
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : t('failedToRestoreConcept')
              );
            }
            queryClient.invalidateQueries({
              queryKey: cacheKeys.concepts.list(projectId),
            });
          },
        },
      });

      router.push(`/project/${projectId}`);
    } catch (error) {
      console.error('Failed to delete concept:', error);
      toast.error(t('failedToDeleteConcept'));
      setIsDeleting(false);
    }
  }, [projectId, conceptId, router, queryClient, onDeleteConcept, t, tCommon]);

  // Open the angle-delete confirmation modal (no immediate delete).
  const openDeleteAngleModal = useCallback(() => {
    setShowDeleteAngleModal(true);
  }, []);

  // Delete angle handler — runs the actual delete mutation (via callback) and
  // closes the confirmation modal. The 409 cross-concept conflict dialog is
  // driven separately by the caller's mutation, after this confirm.
  const handleDeleteAngle = useCallback(() => {
    setShowDeleteAngleModal(false);
    if (onDeleteAngle) {
      onDeleteAngle();
    }
  }, [onDeleteAngle]);

  // Delete render handler — calls callback directly (undo toast is in the hook)
  const handleDeleteRender = useCallback(() => {
    if (!renderId) return;
    if (onDeleteRender) {
      onDeleteRender(renderId);
    }
  }, [renderId, onDeleteRender]);

  // No-op setter for API compatibility
  const noop = useCallback(() => {}, []);

  return {
    // Concept/render deletes stay immediate (undo toast) — no modal.
    showDeleteConceptModal: false,
    showDeleteRenderModal: false,
    // Angle (secondary view) delete is gated behind a real confirmation modal.
    showDeleteAngleModal,

    // Direct actions — concept/render execute delete immediately;
    // angle opens its confirmation modal first.
    openDeleteConceptModal: handleDeleteConcept,
    openDeleteAngleModal,
    openDeleteRenderModal: handleDeleteRender,

    // Concept/render closers are no-ops (no modal); angle uses real state.
    setShowDeleteConceptModal: noop,
    setShowDeleteAngleModal,
    setShowDeleteRenderModal: noop,

    // Actions (same as openers now)
    handleDeleteConcept,
    handleDeleteAngle,
    handleDeleteRender,

    // Loading states
    isDeleting,
  };
}
